import { uid, DASHBOARD_SCHEMA_VERSION, type DashboardConfig } from './types';

/**
 * A ready-made board (name + description + config) for the empty state / worked example.
 *
 * Mirrors the built-in `/dashboard` page panel-for-panel, rebuilt from configurable panels:
 * the global range is Yesterday (so the KPIs, source table, hourly chart and flow lists read
 * the same single day), the two trend panels override to last 14 days, and the AI briefing is
 * a Summary panel. The one deviation the configurable system can't express: the flow panels
 * are tables, not `/dashboard`'s chip lists.
 */
export function buildExampleDashboard(): { name: string; description: string; config: DashboardConfig } {
  const config: DashboardConfig = {
    version: DASHBOARD_SCHEMA_VERSION,
    time_preset: 'yesterday',
    panels: [
      // KPI row — the default's five tiles, as single-metric stat panels (profit leads, wider).
      {
        id: uid('p'), title: 'Profit', viz: 'stat',
        query: { group_by: 'date', metrics: ['profit'], compare: true },
        layout: { x: 0, y: 0, w: 4, h: 3 },
      },
      {
        id: uid('p'), title: 'Revenue', viz: 'stat',
        query: { group_by: 'date', metrics: ['revenue'], compare: true },
        layout: { x: 4, y: 0, w: 2, h: 3 },
      },
      {
        id: uid('p'), title: 'Cost', viz: 'stat',
        query: { group_by: 'date', metrics: ['cost'], compare: true },
        layout: { x: 6, y: 0, w: 2, h: 3 },
      },
      {
        id: uid('p'), title: 'ROAS', viz: 'stat',
        query: { group_by: 'date', metrics: ['roas'], compare: true },
        layout: { x: 8, y: 0, w: 2, h: 3 },
      },
      {
        id: uid('p'), title: 'Conversions', viz: 'stat',
        query: { group_by: 'date', metrics: ['conversions'] },
        layout: { x: 10, y: 0, w: 2, h: 3 },
      },
      // The precomputed daily AI briefing (follows the active workspace).
      {
        id: uid('p'), title: 'What needs attention', viz: 'summary',
        query: { group_by: 'date', metrics: ['profit'] },
        layout: { x: 0, y: 3, w: 12, h: 6 },
      },
      // Trend + the same series as numbers, side by side.
      {
        id: uid('p'), title: 'Profit trend', subtitle: 'Last 14 days', viz: 'line',
        query: { group_by: 'date', metrics: ['profit'], time_preset: 'last_14_days' },
        layout: { x: 0, y: 9, w: 7, h: 8 },
      },
      {
        id: uid('p'), title: 'By date', subtitle: 'Newest first', viz: 'table',
        query: {
          group_by: 'date', metrics: ['cost', 'revenue', 'profit', 'roas'],
          time_preset: 'last_14_days', sort_order: 'desc',
        },
        layout: { x: 7, y: 9, w: 5, h: 8 },
      },
      {
        id: uid('p'), title: 'By traffic source', subtitle: 'Top by spend', viz: 'table',
        query: {
          group_by: 'traffic_source_id',
          metrics: ['cost', 'revenue', 'profit', 'roas', 'conversions'],
          sort_by: 'cost', sort_order: 'desc',
        },
        layout: { x: 0, y: 17, w: 7, h: 8 },
      },
      {
        id: uid('p'), title: 'Profit by hour', subtitle: 'By hour', viz: 'bar',
        query: { group_by: 'hour', metrics: ['profit'] },
        layout: { x: 7, y: 17, w: 5, h: 8 },
      },
      {
        id: uid('p'), title: 'Top flows by profit', subtitle: 'Top 5', viz: 'table',
        query: {
          group_by: 'flow_id', metrics: ['profit', 'cost', 'revenue'],
          sort_by: 'profit', sort_order: 'desc', limit: 5,
        },
        layout: { x: 0, y: 25, w: 6, h: 7 },
      },
      {
        id: uid('p'), title: 'Losing money', subtitle: 'Biggest losses first', viz: 'table',
        query: {
          group_by: 'flow_id', metrics: ['profit', 'cost'],
          sort_by: 'profit', sort_order: 'asc', limit: 5,
        },
        layout: { x: 6, y: 25, w: 6, h: 7 },
      },
    ],
  };
  return { name: 'Overview', description: 'Account overview · mirrors the default dashboard', config };
}
